import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, endpoints } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { CafLogo } from "@/components/CafLogo";
import { Printer, CheckCircle2, Heart, Mail, AlertTriangle } from "lucide-react";
import { SEO } from "@/lib/seo";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "long", timeStyle: "short" }) : "");

export default function ReceiptPage() {
  const { id } = useParams();
  const [receipt, setReceipt] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(endpoints.receipts(id))
      .then(setReceipt)
      .catch((e) => setError(e?.response?.data?.detail || "Receipt not found"));
  }, [id]);

  if (error) {
    return (
      <main className="container-x py-24 text-center">
        <AlertTriangle className="h-10 w-10 mx-auto text-[hsl(var(--orange))]" />
        <h1 className="heading-2 mt-4">Receipt unavailable</h1>
        <p className="lead mt-2" data-testid="receipt-error">{String(error)}</p>
        <Link to="/donate"><Button className="mt-6 rounded-full">Back to Donate</Button></Link>
      </main>
    );
  }

  if (!receipt) {
    return (
      <main className="container-x py-16 max-w-3xl">
        <Skeleton className="h-10 w-64" /><Skeleton className="h-96 mt-6 rounded-2xl" />
      </main>
    );
  }

  const org = receipt.organization || {};
  return (
    <main className="bg-[hsl(var(--surface-tint))] min-h-screen">
      <SEO title="Donation Receipt" description="Your official 80G donation receipt from Citizen Awareness Foundation." />
      <div className="container-x py-10 lg:py-16 max-w-3xl">
        <div className="flex items-center justify-between mb-6 no-print">
          <div className="inline-flex items-center gap-2 text-[hsl(var(--green))] font-semibold"><CheckCircle2 className="h-5 w-5" /> Payment successful</div>
          <Button onClick={() => window.print()} data-testid="receipt-print-button" className="rounded-full bg-[hsl(var(--orange))] hover:bg-[hsl(var(--orange-2))] text-black font-semibold"><Printer className="h-4 w-4 mr-2" /> Print / Save as PDF</Button>
        </div>

        {receipt.mode === "test" && (
          <div className="mb-4 rounded-lg border border-[hsl(var(--orange)/0.4)] bg-[hsl(var(--orange)/0.08)] px-4 py-2.5 text-xs font-semibold text-[hsl(var(--orange))] no-print" data-testid="receipt-test-banner">
            TEST MODE — This is a simulated payment. Add Razorpay keys to enable real transactions.
          </div>
        )}

        <div id="receipt-print" className="rounded-2xl border border-[hsl(var(--border))] bg-card caf-shadow overflow-hidden" data-testid="receipt-card">
          <div className="tricolour-strip-h h-1.5 w-full" aria-hidden="true" />
          <div className="p-8">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <CafLogo />
              <div className="text-right">
                <div className="eyebrow justify-end">80G Donation Receipt</div>
                <div className="font-bold text-[hsl(var(--navy))] mt-1" data-testid="receipt-number">{receipt.receipt_no}</div>
                <div className="text-xs text-[hsl(var(--muted-ink))] mt-0.5">{fmtDate(receipt.paid_at)}</div>
              </div>
            </div>

            <div className="mt-8 rounded-xl bg-[hsl(var(--surface-tint-green))] border border-[hsl(var(--green)/0.2)] p-5 text-center">
              <div className="text-xs uppercase tracking-widest text-[hsl(var(--green))] font-semibold">Donation amount</div>
              <div className="text-4xl font-bold text-[hsl(var(--navy))] mt-1 tabular" data-testid="receipt-amount">₹{Number(receipt.amount_inr).toLocaleString("en-IN")}</div>
              <div className="text-sm text-[hsl(var(--muted-ink))] mt-1">towards {receipt.purpose}</div>
            </div>

            <div className="mt-8 grid gap-6 sm:grid-cols-2 text-sm">
              <div>
                <div className="eyebrow mb-2">Donor details</div>
                <div className="space-y-1.5">
                  <Row k="Name" v={receipt.donor_name} testid="receipt-donor-name" />
                  <Row k="Email" v={receipt.donor_email} />
                  <Row k="PAN" v={receipt.donor_pan} />
                  <Row k="Address" v={receipt.donor_address} />
                </div>
              </div>
              <div>
                <div className="eyebrow mb-2">Payment details</div>
                <div className="space-y-1.5">
                  <Row k="Method" v={String(receipt.method || "").replace("_", " ").toUpperCase()} />
                  <Row k="Payment ID" v={receipt.payment_id} />
                  <Row k="Order ID" v={receipt.order_id} />
                  <Row k="Mode" v={receipt.mode === "test" ? "TEST (simulated)" : "LIVE"} />
                </div>
              </div>
            </div>

            <div className="mt-8 rounded-lg border border-[hsl(var(--border))] p-4 text-xs text-[hsl(var(--muted-ink))] leading-relaxed">
              <strong className="text-[hsl(var(--navy))]">{org.name}</strong> — {org.legal_form || "Section 8 Company (Companies Act, 2013)"} • {org.address}<br />
              CIN: {org.cin} • PAN: {org.pan} • 80G Reg: {org.reg_80g} • 12A Reg: {org.reg_12a} • CSR-1: {org.csr1}<br />
              {receipt.tax_note}
            </div>

            <div className="mt-6 flex items-center justify-between text-xs text-[hsl(var(--muted-ink))]">
              <span>Queries: {org.email} • {org.phone}</span>
              <span className="inline-flex items-center gap-1"><Heart className="h-3.5 w-3.5 text-[hsl(var(--orange))]" /> Thank you for powering change.</span>
            </div>
          </div>
        </div>

        <div className="mt-6 rounded-xl border border-[hsl(var(--border))] bg-card p-4 flex items-start gap-3 text-sm text-[hsl(var(--muted-ink))] no-print" data-testid="receipt-email-note">
          <Mail className="h-4 w-4 mt-0.5 text-[hsl(var(--green))]" />
          <span>A copy of this receipt has been <strong>queued for email</strong> to {receipt.donor_email}. Email delivery activates once our email service is configured.</span>
        </div>

        <div className="mt-6 flex flex-wrap gap-3 no-print">
          <Link to="/donor-wall"><Button variant="outline" className="rounded-full" data-testid="receipt-view-wall">See yourself on the Donor Wall</Button></Link>
          <Link to="/"><Button variant="ghost" className="rounded-full">Back to home</Button></Link>
        </div>
      </div>
    </main>
  );
}

const Row = ({ k, v, testid }) => (
  <div className="flex gap-2">
    <span className="w-24 shrink-0 text-[hsl(var(--muted-ink))]">{k}</span>
    <span className="font-medium text-[hsl(var(--navy))] break-all" data-testid={testid}>{v || "—"}</span>
  </div>
);
